import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Users, Clock, Video, Plus, BarChart3, Settings } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { useMeeting } from "@/hooks/useMeetings"
import { usePolls } from "@/hooks/usePolls"
import { useAuth } from "@/hooks/useAuth"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { PollCard } from "@/components/polls/PollCard"
import { CreatePollModal } from "@/components/polls/CreatePollModal"
import { JitsiMeeting } from "@/components/JitsiMeeting"
import { Layout } from "@/components/Layout"

export default function Reuniao() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { data: meeting, isLoading: meetingLoading } = useMeeting(id!)
  const { polls, isLoading: pollsLoading } = usePolls(id)
  const [showCreatePoll, setShowCreatePoll] = useState(false)
  const [showVideo, setShowVideo] = useState(false)

  const canManage = profile?.role === "sindico" || profile?.role === "administracao"

  useEffect(() => {
    // Auto-show video if meeting is live
    if (meeting?.status === "live") {
      setShowVideo(true)
    }
  }, [meeting?.status])

  if (meetingLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="h-96 bg-gray-200 rounded-lg animate-pulse" />
      </div>
    )
  }

  if (!meeting) {
    return (
      <div className="container mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-4">Reunião não encontrada</h1>
        <Button onClick={() => navigate("/reunioes")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar às Reuniões
        </Button>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "live": return "bg-green-500"
      case "scheduled": return "bg-blue-500"
      case "ended": return "bg-gray-500"
      default: return "bg-gray-500"
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case "live": return "Ao Vivo"
      case "scheduled": return "Agendada"
      case "ended": return "Finalizada"
      default: return "Desconhecido"
    }
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/reunioes")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold">{meeting.title}</h1>
                <Badge className={`${getStatusColor(meeting.status)} text-white`}>
                  {getStatusText(meeting.status)}
                </Badge>
              </div>
              <div className="flex items-center gap-6 text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {format(new Date(meeting.scheduled_date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  Máx. {meeting.max_participants} participantes
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {meeting.status === "live" && (
              <Button 
                onClick={() => setShowVideo(!showVideo)}
                className={showVideo ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
              >
                <Video className="w-4 h-4 mr-2" />
                {showVideo ? "Sair da Reunião" : "Entrar na Reunião"}
              </Button>
            )}
            {canManage && (
              <Button 
                variant="outline"
                onClick={() => setShowCreatePoll(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Enquete
              </Button>
            )}
          </div>
        </div>

        {/* Description */}
        {meeting.description && (
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">{meeting.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Video Conference */}
        {showVideo && meeting.status === "live" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Videoconferência
              </CardTitle>
            </CardHeader>
            <CardContent>
              <JitsiMeeting 
                roomName={meeting.jitsi_room_id}
                userDisplayName={profile?.full_name || "Participante"}
                password={meeting.meeting_password}
              />
            </CardContent>
          </Card>
        )}

        {/* Polls Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              Enquetes
            </h2>
            {canManage && (
              <Button 
                variant="outline"
                onClick={() => setShowCreatePoll(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Enquete
              </Button>
            )}
          </div>

          {pollsLoading ? (
            <div className="grid gap-4">
              {[1, 2].map(i => (
                <div key={i} className="h-48 bg-gray-200 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : polls.length > 0 ? (
            <div className="grid gap-4">
              {polls.map(poll => (
                <PollCard 
                  key={poll.id} 
                  poll={poll} 
                  canManage={canManage}
                  meetingStatus={meeting.status}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <BarChart3 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Nenhuma enquete criada</h3>
                <p className="text-muted-foreground mb-6">
                  {canManage 
                    ? "Crie enquetes para engajar os participantes da reunião."
                    : "Aguarde enquetes serem criadas pelo organizador."
                  }
                </p>
                {canManage && (
                  <Button onClick={() => setShowCreatePoll(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeira Enquete
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {showCreatePoll && (
          <CreatePollModal 
            open={showCreatePoll}
            onOpenChange={setShowCreatePoll}
            meetingId={id!}
          />
        )}
      </div>
    </Layout>
  )
}